import React, { useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { Icon } from "best-common-react";
import UncontrolledInput from "../elements/UncontrolledInput";
import WBCDataTable from "../data-table/WBCDataTable";
import PositionsContext from "../../contexts/PositionsContext";
import TableSortFilterContext from "../../contexts/TableSortFilterContext";
import LinkText from "../roster/components/LinkText";
import RouteConstants from "../../constants/RouteConstants";
import { sortPlayers } from "../roster/RosterSortHelper";
import ClubFormatter from "../table/ClubFormatter";
import OrgsContext from "../../contexts/OrgsContext";
import RequestStatusFormatter from "../table/RequestStatusFormatter";
import CoachingStaffActionsFormatter from "./CoachingStaffActionsFormatter";
import TournamentFormContext from "../../contexts/TournamentFormContext";
import WBCAccessControl from "../protected/WBCAccessControl";
import CoachingStaffConstants from "../../constants/CoachingStaffConstants";

const ROW_HEIGHT = 35;

const NppaIcon = styled(Icon)`
  &:hover {
    color: ${props => props.theme.wbcBlue};
    cursor: default;
  }
`;

const UniformInput = styled(UncontrolledInput).attrs(() => ({
  timeout: 75
}))`
  &&& {
    height: 28px;
    font-size: 14px;
  }
`;

const CoachingStaffTable = ({ isWBCI, data, maxRows, staffType, setUniformUpdates }) => {
  const orgsContext = useContext(OrgsContext);
  const tournamentFormContext = useContext(TournamentFormContext);
  const { onFormDirty } = tournamentFormContext.state;
  const { posIdToAbbrev, posIdToName } = useContext(PositionsContext).state;
  const tableSortFilterContext = useContext(TableSortFilterContext);
  const [rows, setRows] = useState([]);
  const [sortedRows, setSortedRows] = useState([]);
  const [sortFilters, setSortFilters] = useState({ col: "lastCommaFirstName", dir: "ASC" });

  const NppaFormatter = ({ value }) => {
    return value ? <NppaIcon iconName="fa-paperclip" /> : null;
  };

  NppaFormatter.propTypes = {
    value: PropTypes.bool
  };

  const UniformFormatter = ({ row }) => {
    return (
      <WBCAccessControl
        input={isDisabled => (
          <UniformInput
            value={row?.uniformNumber}
            disabled={isDisabled}
            postChange={value => {
              setUniformUpdates(prev => {
                const existingIndex = prev.findIndex(update => update.profileId === row.profileId);
                if (existingIndex !== -1) {
                  // Update existing entry
                  const updated = [...prev];
                  updated[existingIndex] = { profileId: row.profileId, uniformNumber: value };
                  return updated;
                } else {
                  // Add new entry
                  return [...prev, { profileId: row.profileId, uniformNumber: value }];
                }
              });
              onFormDirty();
            }}
          />
        )}
      />
    );
  };

  UniformFormatter.propTypes = {
    row: PropTypes.object
  };

  const StaffLinkFormatter = ({ value, row }) => {
    return (
      <LinkText row={row} profileType={RouteConstants.STAFF}>
        {value}
      </LinkText>
    );
  };

  StaffLinkFormatter.propTypes = {
    value: PropTypes.string,
    row: PropTypes.object
  };

  const PositionFormatter = ({ row }) => {
    return (
      // added name tooltip to show the full position name in case users want to see the full position name
      <div style={{ cursor: "pointer" }} title={row?.positionName}>
        {row?.position}
      </div>
    );
  };

  PositionFormatter.propTypes = {
    row: PropTypes.string
  };

  useEffect(() => {
    const personnelRows = data.map((person, index) => {
      return {
        profileId: person?.profileId,
        fedTeamId: person?.fedTeamId,
        profileType: person?.profileType,
        lastName: person?.fullName?.lastName,
        firstName: person?.fullName?.rosterFirstName || person?.fullName?.firstName,
        lastCommaFirstName: `${person?.fullName?.lastName}, ${person?.fullName?.rosterFirstName ||
          person?.fullName?.firstName}`,
        position: !!person?.customStaffPosition
          ? person?.customStaffPosition
          : posIdToAbbrev?.[person?.stats?.positionId],
        positionName: !!person?.customStaffPosition
          ? person?.customStaffPosition
          : posIdToName?.[person?.stats?.positionId],
        hasNppa: person?.hasNppa,
        uniformNumber: person?.uniformNumber,
        orgCode: orgsContext.getOrgCode(person?.affiliation?.orgId),
        clubString: person?.affiliation?.clubString,
        requestStatus: person?.mlbRequestStatus,
        staffType: staffType,
        customStaffPosition: person?.customStaffPosition
      };
    });
    setRows(personnelRows);
  }, [data, orgsContext, posIdToAbbrev, staffType]);

  useEffect(() => {
    if (sortFilters.dir === "NONE") {
      setSortedRows(rows);
    } else {
      setSortedRows(sortPlayers(sortFilters.col, sortFilters.dir, rows));
    }
  }, [rows, sortFilters]);

  const columns = [
    { name: "", key: "actions", sortable: false, width: 25, formatter: CoachingStaffActionsFormatter },
    { name: "Name", key: "lastCommaFirstName", width: 250, formatter: StaffLinkFormatter, sortable: true },
    { name: "Pos", key: "position", width: 150, sortable: true, formatter: PositionFormatter },
    { name: "NPPA", key: "hasNppa", width: 80, formatter: NppaFormatter, sortable: true },
    { name: "Uniform #", key: "uniform", width: 90, formatter: UniformFormatter, sortable: true },
    { name: "Org", key: "orgCode", width: 80, sortable: true },
    { name: "Club", key: "clubString", width: 250, formatter: ClubFormatter, sortable: true },
    {
      name: isWBCI ? "MLB Request Status" : "MLB Request",
      key: "requestStatus",
      formatter: RequestStatusFormatter,
      sortable: true
    }
  ];

  return (
    <WBCDataTable
      useBCR
      columns={columns}
      headerRowHeight={ROW_HEIGHT - 1}
      rowHeight={ROW_HEIGHT}
      height={(sortedRows?.length + 1 || 1) * ROW_HEIGHT}
      data={sortedRows}
      sortFunc={(col, dir) => {
        setSortFilters({ col, dir });
        tableSortFilterContext.dispatch({
          type: "setSortFilters",
          sortFilters: { col, dir }
        });
      }}
      sortColumn={sortFilters.col}
      sortDirection={sortFilters.dir}
    />
  );
};

CoachingStaffTable.propTypes = {
  isWBCI: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  maxRows: PropTypes.number.isRequired,
  staffType: PropTypes.string.isRequired,
  setUniformUpdates: PropTypes.func.isRequired
};

export default CoachingStaffTable;
